import React, { useState, useRef, type KeyboardEvent } from "react";
import type { FC } from "react";
import UtilityPanel from "../layout/UtilityPanel";
import CorePanelFooter from "../layout/CorePanelFooter";
import CoreLayout from "../layout/CoreLayout";
import { IconCaution, IconCheckmarkGreenSm } from "../icons";
import { useModal } from "../../hooks/useModal";
import Button from "../form/Button";
import { useNavigate } from "react-router-dom";
import { useAppStore } from "../../store/appStore";
import {
    jumpToSessionStatus,
    changeMfaMethod,
    resendMfaCode,
    submitMfaCode,
} from "../../api/api";
import { withErrorBoundary } from "../components/withErrorBoundary";

interface MfaCodeUtilityResponse {
    success: boolean;
    message?: string;
    attempts?: number;
}

interface Props {
    codeType: "SMS" | "EMAIL";
    getSessionId: () => string | null;
    cancelSession: () => void;
    error: boolean;
    utilityResponse: MfaCodeUtilityResponse | null;
}

const MfaCode: FC<Props> = (props) => {
    const {
        setSessionStatus,
        setForceCheckSessionStatus,
        maskedPhoneNumber,
        maskedEmailAddress,
        setUtilityResponse,
    } = useAppStore();
    const { showModal } = useModal();
    const navigate = useNavigate();
    const [code, setCode] = useState<string[]>(Array(6).fill(""));
    // props.error is immutable and serves as the source of truth for whether an error occurred,
    // displayError is mutable and indicates whether the error should be displayed to the user
    const [displayError, setDisplayError] = useState<boolean>(props.error);
    // TODO see if we can display a loading state here
    // const [shouldShowResendCodeStatus, setShouldShowResendCodeStatus] = useState(false);
    const hiddenInputRef = useRef<HTMLInputElement>(null);

    function handleCodeChange(value: string) {
        if (displayError) {
            setDisplayError(false);
        }
        // Only allow numbers
        const numbers = value.replace(/[^0-9]/g, "");
        const newCode = numbers.split("").slice(0, 6);
        setCode([...newCode, ...Array(6 - newCode.length).fill("")]);
        if (newCode.length === 6) {
            setTimeout(() => {
                const completeCode = newCode.join("");
                console.log(`Code in onNext() in MfaCode.tsx: ${completeCode}`);
                const sessionId = props.getSessionId();
                if (sessionId != null) {
                    if (props.error) {
                        jumpToSessionStatus(
                            sessionId,
                            `MFA_CODE_WAITING_${props.codeType}`
                        );
                    }

                    setTimeout(() => {
                        submitMfaCode(sessionId, completeCode);
                    }, 500);
                    setSessionStatus("MFA_CODE_SUBMITTING");
                    setForceCheckSessionStatus(true);
                }
            }, 250);
        }
    }

    function handleInputClick() {
        hiddenInputRef.current?.focus();
    }

    function handleKeyDown(event: KeyboardEvent) {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            hiddenInputRef.current?.focus();
        }
    }

    function onResendCode() {
        const sessionId = props.getSessionId();
        if (sessionId == null) {
            console.error("Session ID is null");
            return;
        }
        resendMfaCode(sessionId);
        setUtilityResponse(null);
        setForceCheckSessionStatus(true);
    }

    function onChangeMfaMethod() {
        const sessionId = props.getSessionId();
        if (sessionId == null) {
            console.error("Session ID is null");
            return;
        }
        setUtilityResponse(null);
        changeMfaMethod(sessionId);
        setForceCheckSessionStatus(true);
    }

    return (
        <CoreLayout>
            <UtilityPanel
                title={`Provide your one-time PG&E authentication code ${
                    props.utilityResponse != null &&
                    props.utilityResponse.attempts != null &&
                    props.utilityResponse.attempts > 1
                        ? `(Attempt ${props.utilityResponse.attempts}/3)`
                        : ""
                }`}
            >
                {props.codeType === "EMAIL" ? (
                    <>
                        <div className="self-stretch justify-start">
                            <span className="text-black text-xs font-normal font-['Inter']">
                                PG&E sent a six-digit authentication code to
                                your email address
                            </span>
                            {maskedEmailAddress && (
                                <span className="text-black whitespace-nowrap text-xs font-semibold font-['Inter']">
                                    {" "}
                                    {maskedEmailAddress}
                                </span>
                            )}
                            <span className="text-black text-xs font-normal font-['Inter']">
                                . Please enter the code below.
                            </span>
                        </div>
                        <div className="w-12 h-4 bg-zinc-300/0" />{" "}
                    </>
                ) : (
                    <>
                        <div className="self-stretch justify-start">
                            <span className="text-black text-xs font-normal font-['Inter']">
                                PG&E sent a six-digit authentication code via
                                SMS text message to your phone number
                            </span>
                            {maskedPhoneNumber && (
                                <span className="text-black whitespace-nowrap text-xs font-semibold font-['Inter']">
                                    {" "}
                                    {maskedPhoneNumber}
                                </span>
                            )}
                            <span className="text-black text-xs font-normal font-['Inter']">
                                . Please enter the code below.
                            </span>
                        </div>
                        <div className="w-12 h-4 bg-zinc-300/0" />
                    </>
                )}

                <div className="self-stretch justify-start text-black text-sm font-semibold font-['Inter']">
                    Authentication code
                </div>
                <fieldset
                    className="inline-flex justify-start items-start gap-2 border-0 p-0 m-0"
                    onClick={handleInputClick}
                    onKeyDown={handleKeyDown}
                    aria-label="Authentication code input"
                >
                    <legend className="sr-only">
                        Authentication code input
                    </legend>
                    <input
                        ref={hiddenInputRef}
                        type="text"
                        className="absolute opacity-0 w-0 h-0"
                        value={code.join("")}
                        onChange={(e) => handleCodeChange(e.target.value)}
                        maxLength={6}
                        aria-label="Hidden authentication code input"
                        autoFocus
                    />
                    {code.map((digit, position) => (
                        <input
                            key={`code-digit-${position}-${digit}`}
                            className="w-9 h-11 px-1 py-2.5 text-xl bg-white rounded-[5px] border border-pge-blue text-center"
                            value={digit}
                            readOnly
                            aria-label={`Digit ${position + 1}`}
                        />
                    ))}
                </fieldset>
                {displayError && (
                    <div className="inline-flex self-stretch justify-start items-center text-red-600 text-xs font-normal font-['Inter'] mt-4">
                        <IconCaution />
                        Incorrect one-time PG&E authentication code, please try
                        again.
                    </div>
                )}
                <div
                    className="self-stretch justify-start text-pge-blue text-xs font-normal font-['Inter'] hover:underline cursor-pointer mt-2"
                    onClick={onChangeMfaMethod}
                >
                    Try again with a different authentication method
                </div>
                {/* <div
                    className="self-stretch justify-start text-pge-blue text-xs font-normal font-['Inter'] hover:underline cursor-pointer mt-2"
                    onClick={onResendCode}
                >
                    Resend code
                </div>
                {utilityResponse != null &&
                utilityResponse.message?.includes("MFA code resend") && (
                utilityResponse?.success ? (
                    <div className="inline-flex self-stretch justify-start items-center text-green-600 text-xs font-normal font-['Inter']">
                        <IconCheckmarkGreenSm className="mr-1" />
                        Code resent
                    </div>
                ) : (
                    <div className="inline-flex self-stretch justify-start items-center text-red-600 text-xs font-normal font-['Inter']">
                        <IconCaution />
                            Code failed to resend, please try again in a few
                            seconds.
                        </div>
                    ))} */}

                {props.utilityResponse != null &&
                    props.utilityResponse.message?.includes(
                        "MFA method change"
                    ) &&
                    !props.utilityResponse.success && (
                        <div className="inline-flex self-stretch justify-start items-center text-red-600 text-xs font-normal font-['Inter']">
                            <IconCaution />
                            Failed to change authentication method, please try again.
                        </div>
                    )}
            </UtilityPanel>
            <CorePanelFooter>
                <Button
                    onClick={() =>
                        showModal(
                            "cancel",
                            "Are you sure you want to cancel?",
                            "Cancelling will end the authorization process.",
                            props.cancelSession
                        )
                    }
                    type="primary"
                    subType="outline"
                >
                    Cancel
                </Button>
            </CorePanelFooter>
        </CoreLayout>
    );
};

export default withErrorBoundary(MfaCode);
